import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Reveal, SectionEyebrow } from '../components/ui-bits';
import { getBlogPost, getBlog } from '../lib/api';
import Seo from '../components/Seo';
import MarkdownBody from '../components/MarkdownBody';

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [suggested, setSuggested] = useState([]);

  useEffect(() => {
    getBlogPost(slug).then(setPost).catch(() => {});
    getBlog().then((all) => setSuggested(all.filter(p => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (!post) return <div className="pt-48 pb-24 text-center text-[#6b6b6b]">Loading…</div>;

  return (
    <article data-testid="blog-post-page" className="bg-white">
      <Seo
        title={post.title}
        description={post.excerpt.slice(0, 300)}
        path={`/green-room/${post.slug}`}
        image={post.cover && post.cover.startsWith('http') ? post.cover : undefined}
        type="article"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt,
        "author": { "@type": "Person", "name": post.author },
        "datePublished": post.published_at,
        "articleSection": post.category,
        "publisher": {
          "@type": "Organization",
          "name": "David Ball Management",
          "logo": { "@type": "ImageObject", "url": "https://dbmtalent.com/favicon.svg" }
        },
        "mainEntityOfPage": `https://dbmtalent.com/green-room/${post.slug}`
      })}} />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-32 lg:pt-40">
        <Link to="/green-room" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#002b5c] gold-underline">
          <ArrowLeft size={14} /> The Green Room
        </Link>
      </div>

      <header className="max-w-[1000px] mx-auto px-6 lg:px-10 pt-14 pb-8 text-center">
        <Reveal>
          <p className="eyebrow">{post.category}</p>
          <h1 className="font-heading uppercase text-[#002b5c] text-4xl sm:text-5xl md:text-6xl lg:text-7xl mt-6 leading-[0.9]">
            {post.title}
          </h1>
          <div className="mt-8 flex items-center justify-center gap-4 text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">
            <span>{post.author}</span>
            <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
            <span>{new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
            <span>{post.read_time}</span>
          </div>
        </Reveal>
      </header>

      <Reveal>
        <div className="max-w-[760px] mx-auto px-6 lg:px-0 flex items-center justify-center py-10">
          <span className="w-16 h-px bg-[#C5A059]" />
        </div>
      </Reveal>

      <div className="max-w-[760px] mx-auto px-6 lg:px-0 pb-20 lg:pb-28 text-lg leading-[1.9] text-[#333]">
        <Reveal>
          <p className="font-heading text-2xl md:text-3xl leading-[1.35] text-[#002b5c] mb-10">{post.excerpt}</p>
          <MarkdownBody body={post.body} className="text-lg" />
        </Reveal>
      </div>

      <section className="bg-[#faf8f4] py-24">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12">
            <SectionEyebrow>Continue reading</SectionEyebrow>
            <Link to="/green-room" className="text-[11px] tracking-[0.22em] uppercase text-[#002b5c] gold-underline hidden md:inline-flex items-center gap-2">
              All articles <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {suggested.map((p, i) => (
              <Link
                key={p.id}
                to={`/green-room/${p.slug}`}
                className="group card-lift bg-white border border-black/8 rounded-2xl p-8 lg:p-10 h-full flex flex-col hover:border-[#C5A059]/60 hover:shadow-[0_20px_60px_-20px_rgba(0,43,92,0.15)] transition-all duration-500"
              >
                <div className="flex items-start justify-between">
                  <p className="eyebrow text-[#6b6b6b]">{p.category}</p>
                  <span className="font-heading text-[#C5A059] text-sm tracking-wider">N°{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="font-heading text-2xl uppercase text-[#002b5c] mt-8 leading-[1.05] group-hover:text-[#C5A059] transition-colors">{p.title}</h3>
                <div className="mt-auto pt-10">
                  <div className="hairline opacity-40 mb-4" />
                  <div className="flex items-center justify-between text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">
                    <span>{p.author}</span>
                    <span>{p.read_time}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
